import React from 'react';
import { Table, Tag, Tooltip } from 'antd';
import { Pencil, Trash2, Tags, ChartColumn, User } from 'lucide-react';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';

const modelLabels = {
    User: 'Usuarios',
    Client: 'Clientes',
    Store: 'Tiendas',
    Product: 'Productos',
    TradeCampaign: 'Campañas',
    TradeContract: 'Contratos'
};

function TagsTable({ tags, loading, onEdit, onDelete, onAssign, onViewSOS }) {
    const columns = [
        {
            title: 'Etiqueta',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center gap-2">
                    <Tag color={record.color} className="!m-0 font-medium">
                        {text}
                    </Tag>
                    {record.hasSosTargets && (
                        <Tooltip title="Tiene objetivos de SOS configurados">
                            <span>
                                <StatusTag tone="violet" dot>
                                    SOS
                                </StatusTag>
                            </span>
                        </Tooltip>
                    )}
                </div>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: 'Descripción',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => <span className="text-sm text-surface-600">{text || '-'}</span>
        },
        {
            title: 'Aplica a',
            dataIndex: 'applicableModels',
            key: 'applicableModels',
            render: (models) => (
                <div className="flex flex-wrap gap-1">
                    {models.map((model) => (
                        <StatusTag key={model} tone="neutral">
                            {modelLabels[model] || model}
                        </StatusTag>
                    ))}
                </div>
            )
        },
        {
            title: 'Usos',
            dataIndex: 'usageCount',
            key: 'usageCount',
            align: 'right',
            render: (count) => <span className={`text-sm tabular-nums ${count > 0 ? 'font-medium text-surface-900' : 'text-surface-400'}`}>{count || 0}</span>,
            sorter: (a, b) => (a.usageCount || 0) - (b.usageCount || 0)
        },
        {
            title: 'Creado por',
            dataIndex: ['createdBy', 'name'],
            key: 'createdBy',
            render: (name) => (
                <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-100 text-xs text-surface-500">
                        <User />
                    </span>
                    <span className="text-sm text-surface-600">{name || '-'}</span>
                </div>
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 150,
            render: (_, record) => (
                <IconActions>
                    {record.hasSosTargets && onViewSOS && <IconAction icon={<ChartColumn />} label="Ver análisis SOS" onClick={() => onViewSOS(record)} />}
                    <IconAction icon={<Tags />} label="Asignar etiqueta" onClick={() => onAssign(record)} />
                    <IconAction icon={<Pencil />} label="Editar" onClick={() => onEdit(record)} />
                    {/* Delete asks for confirmation in the controller (Modal.confirm). */}
                    <IconAction icon={<Trash2 />} label="Eliminar" danger onClick={() => onDelete(record)} />
                </IconActions>
            )
        }
    ];

    return (
        <SectionCard flush title="Etiquetas" description="Etiquetas disponibles, a qué aplican y cuántas veces se usan">
            <div className="overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={tags}
                    loading={loading}
                    rowKey="_id"
                    size="middle"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} etiquetas`
                    }}
                    locale={{
                        emptyText: <EmptyState icon={<Tags />} title="Sin etiquetas" description="Crea una etiqueta para empezar a clasificar elementos" />
                    }}
                />
            </div>
        </SectionCard>
    );
}

export default TagsTable;
